import { useMemo } from "react";
import { Container, Row, Col, Card, Badge, Button } from "react-bootstrap";
import { PageTitle } from "./Controls/PageTitle";
import {
  DataGrid,
  colInstantDate,
  colInstantDateTime,
  colInstantDateUtc,
  colMoneyWithCents,
} from "./Controls/DataGrid";
import { showMoney } from "./Data/Money";
import { OperatorInvoice, OperatorInvoiceStatus } from "./Data/ApiTypes";
import { useLoaderData } from "react-router-dom";
import { dinero } from "dinero.js";
import { USD } from "@dinero.js/currencies";
import { ServerTransferOperatorInvoiceResponse } from "./shared/api/axios-client";

export default function OperatorInvoicesPage() {
  const invoices = useLoaderData() as ServerTransferOperatorInvoiceResponse[];

  const columns = useMemo(() => {
    return [
      { headerName: "Invoice #", field: "invoiceNumber" },
      { headerName: "Facility", field: "facilityName" },
      { headerame: "Amount Due", ...colMoneyWithCents("amountDue") },
      {
        headerName: "Status",
        field: "status",
        cellRenderer: (params: any) => {
          return renderStatusBadge(params.value);
        },
        width: 150,
      },
      {
        headerName: "Due",
        ...colInstantDateUtc<OperatorInvoice>("dueDate"),
        sort: undefined,
        sortIndex: undefined,
      },
      {
        headerName: "Created",
        ...colInstantDateUtc<OperatorInvoice>("createdDate"),
        sort: undefined,
        sortIndex: undefined,
      },
      {
        headerName: "Actions",
        field: "actions",
        cellRenderer: (params: any) => {
          const data: OperatorInvoice = params.data;

          if (
            data.status === OperatorInvoiceStatus.Open ||
            data.status === OperatorInvoiceStatus.Overdue
          ) {
            return (
              <button
                className="btn btn-sm btn-main mb-2"
                onClick={() => {
                  if (data.paymentUrl) {
                    window.open(
                      data.paymentUrl,
                      "_blank",
                      "noopener,noreferrer"
                    );
                  }
                }}
              >
                Pay Now
              </button>
            );
          }
          if (data.status === OperatorInvoiceStatus.Paid && data.paymentUrl) {
            return (
              <button
                className="btn btn-sm btn-secondary mb-2"
                onClick={() =>
                  window.open(data.paymentUrl!, "_blank", "noopener,noreferrer")
                }
              >
                View
              </button>
            );
          }

          return null;
        },
        width: 140,
        sortable: false,
      },
    ] as any;
  }, []);

  function handlePay(invoice: ServerTransferOperatorInvoiceResponse) {
    if (!invoice.paymentUrl) return;
    window.open(invoice.paymentUrl, "_blank", "noopener,noreferrer");
  }

  function renderStatusBadge(status: OperatorInvoiceStatus | undefined) {
    if (status === undefined) return null;

    let cls = "";
    let label = OperatorInvoiceStatus[status];

    switch (status) {
      case OperatorInvoiceStatus.Draft:
        cls = "bg-secondary text-white";
        break;
      case OperatorInvoiceStatus.Open:
        cls = "bg-warning text-dark";
        break;
      case OperatorInvoiceStatus.Paid:
        cls = "bg-success text-white";
        break;
      case OperatorInvoiceStatus.Overdue:
        cls = "bg-danger text-white";
        break;
      case OperatorInvoiceStatus.Canceled:
        cls = "bg-muted text-dark";
        break;
      default:
        cls = "bg-light text-dark";
    }

    return <Badge className={cls}>{label}</Badge>;
  }

  return (
    <Container fluid style={{ paddingLeft: 0, paddingRight: 0 }}>
      <Row style={{ marginLeft: "10px", marginRight: 0 }}>
        <Col>
          <div className="d-flex align-items-start justify-content-between mb-2">
            <div>
              <PageTitle>Invoices</PageTitle>
              {invoices.length > 0 && (
                <div className="text-muted">{`View all finalized invoices for your operator. You can check payment status or pay directly through Stripe.`}</div>
              )}
            </div>
          </div>

          {invoices.length === 0 ? (
            <div className="text-center text-muted py-5">
              <h5>No invoices are available.</h5>
              <div>Invoices will appear here once they've been generated.</div>
            </div>
          ) : (
            <>
              <div className="d-none d-md-block" style={{ height: "60vh" }}>
                <DataGrid
                  rowData={invoices}
                  columnDefs={columns as any}
                  defaultColDef={{
                    resizable: true,
                  }}
                />
              </div>

              <div className="d-block d-md-none mt-3">
                {invoices.map((invoice, idx) => (
                  <Card
                    key={invoice.operatorInvoiceId || idx}
                    className="mb-3 p-2 shadow-sm rounded-4 border-0 mx-3"
                  >
                    <Card.Body className="p-2 p-md-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div>
                          <h6 className="mb-0 fw-bold">
                            Invoice #{invoice.invoiceNumber}
                          </h6>

                          {invoice.facilityName && (
                            <div className="text-muted small">
                              {invoice.facilityName}
                            </div>
                          )}
                        </div>

                        <div>{renderStatusBadge(invoice.status)}</div>
                      </div>

                      <div className="row g-2 text-center mb-2">
                        <div className="col-4">
                          <div className="bg-light rounded-3 py-2 px-2">
                            <div className="text-muted small">AMOUNT</div>
                            <div className="fw-bold fs-6">
                              {showMoney(
                                dinero({
                                  amount: invoice.amountDue!,
                                  currency: USD,
                                })
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="col-4">
                          <div className="bg-light rounded-3 py-2 px-2">
                            <div className="text-muted small">DUE</div>
                            <div className="fw-bold fs-6">
                              {invoice.dueDate
                                ? new Date(
                                    invoice.dueDate as any
                                  ).toLocaleDateString()
                                : "—"}
                            </div>
                          </div>
                        </div>

                        <div className="col-4">
                          <div className="bg-light rounded-3 py-2 px-2">
                            <div className="text-muted small">CREATED</div>
                            <div className="fw-bold fs-6">
                              {invoice.createdDate
                                ? new Date(
                                    invoice.createdDate as any
                                  ).toLocaleDateString()
                                : "—"}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="d-flex gap-2">
                        {(invoice.status === OperatorInvoiceStatus.Open ||
                          invoice.status === OperatorInvoiceStatus.Overdue) && (
                          <Button
                            className="btn-main flex-fill"
                            onClick={() => handlePay(invoice)}
                          >
                            Pay Now
                          </Button>
                        )}

                        {invoice.status === OperatorInvoiceStatus.Paid &&
                          invoice.paymentUrl && (
                            <Button
                              className="btn btn-secondary flex-fill"
                              onClick={() =>
                                window.open(
                                  invoice.paymentUrl!,
                                  "_blank",
                                  "noopener,noreferrer"
                                )
                              }
                            >
                              View
                            </Button>
                          )}
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            </>
          )}
        </Col>
      </Row>
    </Container>
  );
}
